import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';

// Helper function to get Authorization Config
const getAuthConfig = () => {
    const token = localStorage.getItem('accessToken');
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

// Separate function to fetch supervisors directly without using supervisorService
const fetchSupervisors = async () => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        const response = await fetch(`${BASE_URL}/supervisors/`, {
            headers: headers
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch supervisors: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching supervisors:', error);
        return [];
    }
};

export const validateEmploymentFormData = (formData) => {
    const errors = {};

    // Required field validations with enhanced messages
    if (!formData.function || formData.function.trim() === '') {
        errors.function = 'Function is required and cannot be empty';
    }

    if (!formData.section || formData.section.trim() === '') {
        errors.section = 'Section is required and cannot be empty';
    }

    if (!formData.unit || formData.unit.trim() === '') {
        errors.unit = 'Unit is required and cannot be empty';
    }

    if (!formData.duty_station_id || formData.duty_station_id.toString().trim() === '') {
        errors.duty_station_id = 'Duty Station is required and must be selected';
    }

    if (!formData.type_of_contract || formData.type_of_contract.trim() === '') {
        errors.type_of_contract = 'Type of Contract is required and cannot be empty';
    }

    // Optional field validation for office_location (basic length check if provided)
    if (formData.office_location && formData.office_location.length > 255) {
        errors.office_location = 'Office location must be less than 255 characters';
    }

    // Conditional validation for expiration date
    if (formData.type_of_contract && !formData.expiration_date) {
        errors.expiration_date = 'Expiration date is required when contract type is specified';
    }

    // Validate date format if a date is provided
    if (formData.expiration_date) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(formData.expiration_date)) {
            errors.expiration_date = 'Invalid date format. Use YYYY-MM-DD';
        } else {
            const date = new Date(formData.expiration_date);
            if (isNaN(date.getTime())) {
                errors.expiration_date = 'Invalid date';
            }
        }
    }

    return errors;
};

// Sanitize Payload
export const sanitizePayload = (data) => {
    const sanitizedData = { ...data };
    Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === '') {
            sanitizedData[key] = null;
        }
    });
    return sanitizedData;
};

// Fetch Employment Data
export const fetchEmploymentData = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/employment/me`, getAuthConfig());
        return response.data; // Return employment data
    } catch (error) {
        if (error.response?.status === 404) {
            // No employment record found
            return null;
        }
        console.error('Error fetching employment data:', error);
        throw new Error('Failed to fetch employment information.');
    }
};

// Format supervisor data before saving
const formatSupervisorData = async (formData) => {
    const updatedFormData = { ...formData };

    // If supervisor is an ID, fetch the supervisor name
    if (updatedFormData.supervisor && !isNaN(updatedFormData.supervisor)) {
        try {
            // Use the local fetchSupervisors function instead of supervisorService
            const supervisors = await fetchSupervisors();
            const supervisor = supervisors.find(s => s.id === parseInt(updatedFormData.supervisor));

            if (supervisor) {
                // Format as "Last Name, First Name"
                updatedFormData.supervisor = `${supervisor.last_name}, ${supervisor.first_name}`;
            }
        } catch (error) {
            console.error('Error fetching supervisor data:', error);
            // If there's an error, keep the ID as is
        }
    }

    return updatedFormData;
};

// Save Employment Data
export const saveEmploymentData = async (formData, currentUser) => {
    try {
        // Format supervisor data
        const formattedData = await formatSupervisorData(formData);
        const sanitizedData = { ...formattedData, user_id: currentUser.id };

        if (sanitizedData.id) {
            // Update existing record
            await axios.put(`${BASE_URL}/employment/${sanitizedData.id}`, sanitizedData, getAuthConfig());
        } else {
            // Create a new record
            await axios.post(`${BASE_URL}/employment`, sanitizedData, getAuthConfig());
        }
    } catch (error) {
        console.error('Error saving employment data:', error);
        throw new Error('Failed to save employment information.');
    }
};

// Delete Employment Data
export const deleteEmploymentData = async (employmentId) => {
    try {
        const response = await axios.delete(`${BASE_URL}/employment/${employmentId}`, getAuthConfig());
        return response.data;
    } catch (error) {
        console.error('Error deleting employment data:', error);
        throw new Error('Failed to delete employment record.');
    }
};

// Get supervisor by ID
export const getSupervisorById = async (supervisorId) => {
    try {
        // Use the local fetchSupervisors function instead of supervisorService
        const supervisors = await fetchSupervisors();
        return supervisors.find(s => s.id === parseInt(supervisorId)) || null;
    } catch (error) {
        console.error('Error fetching supervisor by ID:', error);
        throw new Error('Failed to fetch supervisor information.');
    }
};